import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { motion } from "framer-motion"
import { Heart } from "lucide-react"
import { toast } from "sonner"
import { useAddFavorite, useFavoriteIds, useRemoveFavorite } from "@/entities/favorite"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { scalePop } from "@/shared/lib/motion"
import { Button } from "@/shared/ui/button"

interface FavoriteButtonProps {
	postId: string
	variant?: "default" | "icon"
	size?: "default" | "sm" | "lg" | "icon"
}

export function FavoriteButton({
	postId,
	variant = "default",
	size = "default",
}: FavoriteButtonProps) {
	const { data: favoriteIds = [] } = useFavoriteIds()
	const addMutation = useAddFavorite()
	const removeMutation = useRemoveFavorite()
	const { triggerNotification } = useHaptic()

	const isFavorite = favoriteIds.includes(postId)
	const isLoading = addMutation.isPending || removeMutation.isPending

	const handleToggle = async (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		try {
			if (isFavorite) {
				await removeMutation.mutateAsync(postId)
			} else {
				await addMutation.mutateAsync(postId)
				triggerNotification("success")
			}
		} catch (error) {
			triggerNotification("error")
			toast.error(
				extractApiErrorMessage(
					error,
					isFavorite ? "Не удалось удалить из избранного" : "Не удалось добавить в избранное",
				),
			)
		}
	}

	if (variant === "icon") {
		return (
			<Button
				type="button"
				variant="ghost"
				size="icon"
				aria-pressed={isFavorite}
				aria-label={isFavorite ? "Удалить из избранного" : "Добавить в избранное"}
				onClick={handleToggle}
				disabled={isLoading}
				className={`min-h-11 min-w-11 rounded-full p-2 hover:bg-transparent ${
					isFavorite
						? "text-red-500 hover:text-red-600"
						: "text-muted-foreground hover:text-foreground"
				}`}
			>
				<motion.span
					variants={scalePop}
					initial={false}
					animate={isFavorite ? "pop" : "idle"}
					className="flex"
				>
					<Heart
						className={`h-5 w-5 ${isFavorite ? "fill-current" : ""}`}
						aria-label={isFavorite ? "Удалить из избранного" : "Добавить в избранное"}
					/>
				</motion.span>
			</Button>
		)
	}

	return (
		<Button
			variant={isFavorite ? "default" : "outline"}
			size={size}
			onClick={handleToggle}
			isLoading={isLoading}
			className="w-full"
		>
			<Heart className={`mr-2 h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
			{isFavorite ? "В избранном" : "Добавить в избранное"}
		</Button>
	)
}
